// src/Pages/Onboarding/Cafe24Onboarding.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../api';
import {
    Check, Copy, Loader2, AlertCircle, Zap, Code,
    ExternalLink, X, Users
} from 'lucide-react';

const VALIDATION_TIMEOUT = 10000;
const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzln4YMAbLsLxbpSvlEgN9D3er1am0YosIubXqnTV5UCzN4PWLYpg3CPO0MzQgB3kHS/exec';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 온보딩 미팅 신청 — 기존 FreeStart와 같은 구글 시트(Apps Script)로 전송
// 실패해도 온보딩 진행은 막지 않음
const requestOnboardingMeeting = async ({ email, mallId }) => {
    try {
        await fetch(SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors',
            redirect: 'follow',
            body: JSON.stringify({
                companyName: mallId || '',
                name: '',
                position: '',
                email,
                phone: '',
                companySize: '',
                industry: '',
                preferredContact: 'email',
                preferredTime: '',
                inquiry: '카페24 온보딩 미팅 신청 (전환율 개선 포인트 찾기)',
                howDidYouHear: 'cafe24_onboarding',
                formType: 'onboarding_meeting'
            })
        });
    } catch (err) {
        console.error('Meeting request error:', err);
    }
};

const Cafe24Onboarding = () => {
    const [step, setStep] = useState('select'); // select | auto | manual | done
    const [info, setInfo] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);

    // 자동 설치 상태
    const [autoStatus, setAutoStatus] = useState('idle'); // idle | loading | success | error
    const [autoErrorMsg, setAutoErrorMsg] = useState('');

    // 수동 설치 상태
    const [scriptCopied, setScriptCopied] = useState(false);
    const [verifiedManually, setVerifiedManually] = useState(false);

    // 시작하기 상태
    const [completing, setCompleting] = useState(false);

    // 설치 경로 추적 (done에서 뒤로가기용)
    const [installPath, setInstallPath] = useState('manual'); // auto | manual

    // ----- 진입 시 정보 로드 -----
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('cafe24/onboarding-info/');
                setInfo(res.data); // { project_code, script_src, mall_id, email? }
            } catch (err) {
                setPageError('온보딩 정보를 불러오지 못했습니다. 새로고침 해주세요.');
            } finally {
                setPageLoading(false);
            }
        })();
    }, []);

    // ----- 자동 설치 핸들러 -----
    const handleAutoInstall = async () => {
        setInstallPath('auto');
        setStep('auto');
        setAutoStatus('loading');
        try {
            await api.post('cafe24/install-script/');
            setAutoStatus('success');
        } catch (err) {
            setAutoStatus('error');
            setAutoErrorMsg(err?.response?.data?.detail || '설치 중 오류가 발생했습니다.');
        }
    };

    // ----- 시작하기 핸들러 -----
    // 실패 시 에러를 던져 StepDone에서 인라인으로 표시 (입력값 유지)
    // 미팅 신청은 온보딩 완료 성공 후에만 전송 (실패 후 재시도 시 중복 신청 방지)
    const handleStart = async ({ email, wantsMeeting }) => {
        setCompleting(true);
        let redirectUrl;
        try {
            const res = await api.post('cafe24/complete-onboarding/', { email });
            redirectUrl = res.data.redirect_url;
        } catch (err) {
            setCompleting(false);
            throw new Error(err?.response?.data?.detail || '완료 처리에 실패했습니다. 다시 시도해주세요.');
        }

        if (wantsMeeting) {
            await requestOnboardingMeeting({ email, mallId: info?.mall_id });
        }
        window.location.href = redirectUrl;
    };

    // ----- 스크립트 복사 -----
    const handleCopyScript = () => {
        const tag = `<script src="${info.script_src}"></script>`;
        navigator.clipboard.writeText(tag);
        setScriptCopied(true);
        setTimeout(() => setScriptCopied(false), 2000);
    };

    // ----- 로딩/에러 처리 -----
    if (pageLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-red-600">{pageError}</div>
            </div>
        );
    }

    // ----- 본문 -----
    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <h1 className="text-2xl font-bold text-gray-900">SWITCHUP 시작하기</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        쇼핑몰에 스크립트를 설치하면 데이터 수집이 시작됩니다.
                    </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    {step === 'select' && (
                        <StepSelect
                            onAuto={handleAutoInstall}
                            onManual={() => {
                                setInstallPath('manual');
                                setStep('manual');
                            }}
                        />
                    )}

                    {step === 'auto' && (
                        <StepAuto
                            status={autoStatus}
                            errorMsg={autoErrorMsg}
                            onNext={() => setStep('done')}
                            onSwitchToManual={() => {
                                setInstallPath('manual');
                                setStep('manual');
                            }}
                            onRetry={handleAutoInstall}
                        />
                    )}

                    {step === 'manual' && (
                        <StepManual
                            info={info}
                            copied={scriptCopied}
                            onCopy={handleCopyScript}
                            verified={verifiedManually}
                            onVerified={() => setVerifiedManually(true)}
                            onNext={() => setStep('done')}
                            onBack={() => setStep('select')}
                        />
                    )}

                    {step === 'done' && (
                        <StepDone
                            info={info}
                            completing={completing}
                            onStart={handleStart}
                            onBack={() => setStep(installPath)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// ===== Step 1: 설치 방법 선택 =====
const StepSelect = ({ onAuto, onManual }) => (
    <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">설치 방법을 선택하세요</h2>
        <p className="text-sm text-gray-500 mb-6">
            설치 위치에 따라 스크립트 실행 속도와 성능이 결정됩니다.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 수동 설치 (추천) */}
            <button
                onClick={onManual}
                className="text-left p-6 border-2 border-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all group relative"
            >
                <div className="absolute top-3 right-3 px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded">
                    추천
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
                    <Code className="w-5 h-5 text-blue-600" />
                </div>
                <div className="font-semibold text-gray-900 mb-1">수동 설치 (권장)</div>
                <div className="text-sm text-gray-600">
                    <code className="bg-white px-1 rounded">&lt;head&gt;</code> 영역에 설치합니다. <strong>스크립트가 가장 빠르게 실행되어 성능이 더 좋습니다.</strong>
                </div>
            </button>

            {/* 자동 설치 */}
            <button
                onClick={onAuto}
                className="text-left p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
            >
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
                    <Zap className="w-5 h-5 text-gray-600" />
                </div>
                <div className="font-semibold text-gray-900 mb-1">자동 설치</div>
                <div className="text-sm text-gray-600">
                    <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> 하단에 설치합니다. <strong>스크립트가 늦게 실행되어 성능이 다소 떨어질 수 있습니다.</strong>
                </div>
            </button>
        </div>
    </div>
);

// ===== Step 2A: 자동 설치 진행 =====
const StepAuto = ({ status, errorMsg, onNext, onSwitchToManual, onRetry }) => (
    <div className="text-center py-8">
        {status === 'loading' && (
            <>
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-900">스크립트 설치 중...</h2>
                <p className="text-sm text-gray-500 mt-1">잠시만 기다려주세요.</p>
            </>
        )}

        {status === 'success' && (
            <>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">설치가 완료되었습니다</h2>
                <p className="text-sm text-gray-500 mt-1 mb-6">
                    쇼핑몰의 모든 페이지에 SWITCHUP 스크립트가 적용되었습니다.
                </p>
                <button
                    onClick={onNext}
                    className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
                >
                    다음
                </button>
            </>
        )}

        {status === 'error' && (
            <>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">설치에 실패했습니다</h2>
                <p className="text-sm text-gray-500 mt-1 mb-6">{errorMsg}</p>
                <div className="flex gap-2 justify-center">
                    <button
                        onClick={onRetry}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
                    >
                        다시 시도
                    </button>
                    <button
                        onClick={onSwitchToManual}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                    >
                        수동 설치로 전환
                    </button>
                </div>
            </>
        )}
    </div>
);

// ===== Step 2B: 수동 설치 =====
const StepManual = ({ info, copied, onCopy, verified, onVerified, onNext, onBack }) => {
    const scriptTag = `<script src="${info.script_src}"></script>`;

    // ----- 검증 로직 (ScriptValidator 참고) -----
    const [url, setUrl] = useState('');
    const [validationStatus, setValidationStatus] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const iframeRef = useRef(null);
    const timeoutRef = useRef(null);
    const urlRef = useRef('');

    const handleMessage = useCallback((event) => {
        try {
            const targetUrl = new URL(urlRef.current);
            if (event.origin !== targetUrl.origin) {
                console.warn('Unauthorized message origin:', event.origin);
                return;
            }

            if (!event.data?.result) return;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            setValidationStatus(event.data.result);
            setIsValidating(false);

            if (event.data.result.success) {
                onVerified(); // 부모에 성공 알림
            }

            if (iframeRef.current) {
                iframeRef.current.onload = null;
                iframeRef.current.src = '';
            }

            window.removeEventListener('message', handleMessage);
        } catch (error) {
            console.error('Message handling error:', error);
            setValidationStatus({
                success: false,
                message: '검증 처리 중 오류가 발생했습니다.'
            });
        }
    }, [onVerified]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    const validateUrl = (inputUrl) => {
        try {
            const parsedUrl = new URL(inputUrl);
            return parsedUrl.protocol === 'https:'
        } catch {
            return false;
        }
    };

    const handleValidation = () => {
        try {
            if (!validateUrl(url)) {
                throw new Error('https://로 시작하는 쇼핑몰 주소를 입력해주세요. (예: https://www.myshop.com)');
            }

            setIsValidating(true);
            setValidationStatus(null);
            urlRef.current = url;

            const targetUrl = new URL(url);
            targetUrl.searchParams.append('verifyscript', 'true');
            targetUrl.searchParams.append('projectId', info.project_code);

            window.addEventListener('message', handleMessage);

            if (iframeRef.current) {
                iframeRef.current.src = targetUrl.toString();
                iframeRef.current.onload = () => {
                    setTimeout(() => {
                        try {
                            iframeRef.current.contentWindow.postMessage(
                                { projectId: info.project_code },
                                targetUrl.origin
                            );

                            timeoutRef.current = setTimeout(() => {
                                setValidationStatus({
                                    success: false,
                                    message: '스크립트가 설치되어 있지 않습니다. 설치 후 다시 시도해주세요.'
                                });
                                setIsValidating(false);
                                window.removeEventListener('message', handleMessage);
                            }, VALIDATION_TIMEOUT);
                        } catch (error) {
                            console.error('PostMessage error:', error);
                            setValidationStatus({
                                success: false,
                                message: '스크립트 검증 중 오류가 발생했습니다.'
                            });
                            setIsValidating(false);
                            window.removeEventListener('message', handleMessage);
                        }
                    }, 1000);
                };
            }
        } catch (error) {
            setValidationStatus({
                success: false,
                message: error.message || '알 수 없는 오류가 발생했습니다.'
            });
            setIsValidating(false);
        }
    };

    return (
        <div>
            <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
                ← 뒤로
            </button>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">수동으로 스크립트 설치</h2>
            <p className="text-sm text-gray-500 mb-6">
                아래 스크립트를 카페24 어드민에서 추가해주세요.
            </p>

            {/* 스크립트 태그 */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">스크립트 태그</label>
                <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-md text-sm overflow-x-auto">
                        <code>{scriptTag}</code>
                    </pre>
                    <button
                        onClick={onCopy}
                        className="absolute top-2 right-2 px-3 py-1.5 bg-gray-700 text-gray-100 text-xs rounded hover:bg-gray-600 flex items-center gap-1"
                    >
                        {copied ? (
                            <>
                                <Check className="w-3 h-3" /> 복사됨
                            </>
                        ) : (
                            <>
                                <Copy className="w-3 h-3" /> 복사
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* 설치 가이드 */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-md">
                <div className="text-sm font-medium text-blue-900 mb-2">설치 위치</div>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>카페24 어드민 → 쇼핑몰 설정 → 기본 설정</li>
                    <li>검색엔진 최적화(SEO) → 코드 직접 입력</li>
                    <li><strong>PC 쇼핑몰</strong> 탭의 <code className="bg-blue-100 px-1 rounded">Head 태그 소스</code>에 붙여넣기</li>
                    <li><strong>모바일 쇼핑몰</strong> 탭에도 동일하게 붙여넣기 → 저장</li>
                </ol>
                <p className="mt-3 text-xs text-blue-700">
                    💡 Head에 설치하면 페이지 렌더링 전에 스크립트가 로드되어 더 정확한 측정이 가능합니다.
                </p>
            </div>

            {/* 검증 영역 */}
            <div className="mt-6 space-y-4">
                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                            스크립트 설치 확인
                        </label>
                        <input
                            type="url"
                            id="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <button
                        onClick={handleValidation}
                        disabled={isValidating || !url}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isValidating ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                확인 중...
                            </>
                        ) : (
                            <>
                                <ExternalLink className="w-4 h-4" />
                                설치 확인
                            </>
                        )}
                    </button>
                </div>

                <iframe
                    ref={iframeRef}
                    className="w-full h-64 border-0"
                    title="script-validation"
                    style={{ display: isValidating ? 'block' : 'none' }}
                />

                {validationStatus && (
                    <div
                        className={`flex items-center gap-2 p-4 rounded-md ${validationStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                            }`}
                        role="alert"
                    >
                        {validationStatus.success ? (
                            <Check className="w-5 h-5 text-green-500" />
                        ) : (
                            <X className="w-5 h-5 text-red-500" />
                        )}
                        {validationStatus.message}
                    </div>
                )}
            </div>

            {/* 다음 버튼 */}
            <div className="mt-8 flex justify-end">
                <button
                    onClick={onNext}
                    className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
                >
                    다음
                </button>
            </div>
        </div>
    );
};

// ===== Step 3: 완료 + 이메일·약관 동의(필수) + 온보딩 미팅(선택) =====
const StepDone = ({ info, completing, onStart, onBack }) => {
    // 이메일 / 약관 동의 (일반 회원가입과 동일 구조)
    const [email, setEmail] = useState(info?.email || '');
    const [agreed, setAgreed] = useState(false);
    // 온보딩 미팅 — 체크하면 위 이메일로 연락
    const [wantsMeeting, setWantsMeeting] = useState(false);
    const [startError, setStartError] = useState('');

    const handleStartClick = async () => {
        const trimmed = email.trim();
        if (!EMAIL_RE.test(trimmed)) {
            setStartError('유효한 이메일 주소를 입력해주세요.');
            return;
        }
        if (!agreed) {
            setStartError('이용약관 및 개인정보처리방침에 동의해주세요.');
            return;
        }
        setStartError('');
        try {
            await onStart({ email: trimmed, wantsMeeting });
        } catch (err) {
            setStartError(err.message);
        }
    };

    return (
        <div className="py-4">
            <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
                ← 뒤로
            </button>

            {/* 완료 헤더 */}
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">모든 준비가 끝났습니다</h2>
                <p className="text-sm text-gray-600">이제 SWITCHUP을 시작할 수 있습니다.</p>
            </div>

            {/* 이메일 + 약관 동의 (필수) */}
            <div className="mb-6">
                <label htmlFor="onboarding-email" className="block text-sm font-medium text-gray-700 mb-1">
                    이메일 <span className="text-red-500">*</span>
                </label>
                <input
                    id="onboarding-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (startError) setStartError('');
                    }}
                    placeholder="name@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <label className="mt-4 flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => {
                            setAgreed(e.target.checked);
                            if (startError) setStartError('');
                        }}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                        SWITCHUP{' '}
                        <a href="https://switchuplab.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            이용약관
                        </a>{' '}
                        및{' '}
                        <a href="https://switchuplab.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            개인정보처리방침
                        </a>
                        에 동의합니다.
                    </span>
                </label>
            </div>

            {/* 온보딩 미팅 (선택) — 체크 한 번으로 신청 */}
            <label
                className={`mb-8 flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${wantsMeeting
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-blue-200 bg-white hover:border-blue-400 hover:bg-blue-50'
                    }`}
            >
                <input
                    type="checkbox"
                    checked={wantsMeeting}
                    onChange={(e) => setWantsMeeting(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                        <Users className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span>
                            20분 화상 미팅으로 우리 몰 전환율 개선 포인트 찾기{' '}
                            <span className="font-normal text-blue-600">(무료)</span>
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                        체크하시면 24시간 내에 위 이메일로 담당자가 연락드려 미팅 일정을 조율해 드려요.
                    </p>
                </div>
            </label>

            {/* 시작하기 */}
            <div className="text-center">
                <button
                    onClick={handleStartClick}
                    disabled={completing || !email.trim() || !agreed}
                    className="px-8 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                    {completing ? (
                        <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> 이동 중...
                        </span>
                    ) : (
                        'SWITCHUP 시작하기'
                    )}
                </button>
                {startError && (
                    <p className="mt-2 text-sm text-red-600">{startError}</p>
                )}
            </div>
        </div>
    );
};

export default Cafe24Onboarding;